import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Tv, Heart, Target } from "lucide-react";
import StreamingChannelsModal from "@/components/StreamingChannelsModal";

// Channel-icon strip — visual shorthand for what's actually in the package.
// Renders one or two lime-tinted pill icons (Streaming TV, PMAX, Social).
const ChannelStrip = ({ channels = [] }) => {
  const ICONS = {
    "Streaming TV": Tv,
    "PMAX Ads": Target,
    "Social Spotlight": Heart,
  };
  return (
    <div
      className="flex flex-wrap gap-1.5 mb-3"
      data-testid="package-channel-strip"
    >
      {channels.map((c) => {
        const Icon = ICONS[c];
        return (
          <span
            key={c}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--af-lime)]/10 border border-[var(--af-lime)]/35 text-[11px] text-[var(--af-cream)]"
          >
            {Icon && <Icon size={12} className="text-[var(--af-lime)]" />}
            {c}
          </span>
        );
      })}
    </div>
  );
};

const HS_TIERS = [
  {
    key: "top-of-mind",
    name: "TV Broadcast",
    legacy: "REACH",
    price: "$4,500",
    minLabel: "Our MIN Streaming TV package",
    channels: ["Streaming TV"],
    bestFor:
      "Streaming TV: highest impact, best for reach and awareness.",
    tagline: "Brand recall for when they need you most.",
    bullets: [
      "111,000 streaming TV ads on premium inventory across popular streaming services",
      "Run on HGTV, ESPN, Weather, Food Network, and local news, homeowners hire the brand they recognize",
      "Fraud-Free Inventory",
      "Professional 15- or 30-second video spot available through our video production services",
      "Detailed channel + impression report (compare to other providers!)",
    ],
  },
  {
    key: "call-now",
    name: "Desktop & Mobile Ads",
    legacy: "ACTION",
    price: "$4,500",
    minLabel: "Our MIN PMAX Ads package",
    channels: ["PMAX Ads"],
    bestFor:
      "PMAX Ads: high-intent buyers across all of Google.",
    tagline: "Make the phone ring this week.",
    bullets: [
      "Runs across Google Search, YouTube, Display, Discover, Gmail, and Maps",
      "Google AI optimizes creative and placement in real time",
      "Three targeting strategies: Search Themes, Category Targeting, and Retargeting",
      "Geo-target by city, county, zip code, or custom radius",
      "Cost-per-event: focused on calls, form fills, and bookings",
      "Retargets past website visitors across every Google channel",
    ],
  },
  {
    key: "social-spotlight",
    name: "Social Media",
    legacy: "PAID SOCIAL",
    price: "$4,500",
    minLabel: "Our MIN Paid Social package",
    channels: ["Social Spotlight"],
    bestFor:
      "Social Media: drive real results with a paid social media strategy.",
    tagline: "Organic social is dead. This is what works now.",
    taglineLinkText: "Here's why we say this",
    taglineLinkHref: "#why-organic-social",
    bullets: [
      "180,000 paid impressions across all available inventory on Facebook + Instagram",
      "15+ different designs and placements (Feed · Reels · Stories · Explore · Marketplace)",
      "Drives traffic straight to your website, trackable in Google Analytics",
      "No followers required, paid reach works day one",
      "No heavy lifting for content creation, we're here to make it easy",
      "Creative refresh every 2 weeks to beat ad fatigue",
    ],
  },
  {
    key: "market-dominator",
    name: "Omni-Channel",
    legacy: "FULL FUNNEL",
    price: "$9,000",
    channels: ["Streaming TV", "PMAX Ads"],
    bestFor:
      "Omni-Channel: combine Streaming TV with PMAX Ads.",
    tagline: "Own your market top to bottom.",
    bullets: [
      "Streaming TV for brand awareness on the big screen",
      "PMAX Ads across Google Search, YouTube, Display, Discover, and Gmail",
      "Google AI optimizes performance and creative in real time",
      "First they learn about you on TV, then Google closes the loop",
      "Advertising the way it was meant to be done!",
    ],
    greenCallout: {
      title: "Omni Channel Starter Package",
      body:
        "Two strategies run concurrently, Streaming TV and PMAX Ads. Full customer journey: discovery to conversion.",
    },
  },
];

const VERTICAL_CONFIG = {
  general: {
    sectionEyebrow: "Starter Packages · Under $10K",
    sectionHeading: (
      <>
        Time-tested campaigns.{" "}
        <span className="text-[var(--af-lime)]">Ready when you are.</span>
      </>
    ),
    sectionSub:
      "We've hand-crafted four starter packages based on years of successful launches for home services and tourism brands. You skip the trial-and-error, we've already done it. Pick a package, we handle the strategy, production, buy, and reporting, and you go back to running the business. No obligation to continue beyond your first campaign.",
    tiers: HS_TIERS,
  },
  "home-services": {
    sectionEyebrow: "90-Day Digital Packages · Under $10K",
    sectionHeading: (
      <>
        Own your busy season in{" "}
        <span className="text-[var(--af-lime)]">90 days.</span>
      </>
    ),
    sectionSub:
      "We've put together four time-tested advertising packages for the Home Services industry so you can work on your business and let the advertising work for you in the background. We take the guesswork out of your question of \"what works?\" today, and we give you confidence in your ad spend. We have been doing this long enough to see results, and we'll show you exactly how to win with your campaign message in just 90 days. After that? Let's have a conversation about a 12-month campaign! (but, there is no obligation to continue with us!)",
    tiers: HS_TIERS,
  },
  tourism: {
    sectionEyebrow: "90-Day Digital Packages · Under $10K",
    sectionHeading: (
      <>
        Own the season in{" "}
        <span className="text-[var(--af-lime)]">90 days.</span>
      </>
    ),
    sectionSub:
      "We've put together four time-tested advertising packages for the Tourism industry, DMOs, wineries, hotels, attractions, restaurants, and events, so you can focus on the visitor experience and let the advertising work for you in the background. We take the guesswork out of your question of \"what works?\" today, and we give you confidence in your ad spend. We have been doing this long enough to see results, and we'll show you exactly how to win with your campaign message in just 90 days. We also built these packages with $5,000+ Tourism Grant Funds in mind. After that? Let's have a conversation about a 12-month campaign! (but, there is no obligation to continue with us!)",
    tiers: [
      {
        key: "top-of-mind",
        name: "TV Broadcast",
        legacy: "REACH",
        price: "$4,500",
        minLabel: "Our MIN Streaming TV package",
        channels: ["Streaming TV"],
        bestFor:
          "Streaming TV: highest impact, best for reach and awareness.",
        tagline: "Be the destination they remember.",
        bullets: [
          "111,000 streaming ads on premium travel inventory",
          "Run on Travel Channel, HGTV, Food Network, and local news, where travelers actually plan their trips",
          "Target by Zip Code, Household Income, and more",
          "Fraud-Free Inventory",
          "Professional 15- or 30-second video spot available",
          "Detailed channel + impression + reach report",
        ],
      },
      {
        key: "call-now",
        name: "Desktop & Mobile Ads",
        legacy: "ACTION",
        price: "$4,500",
        minLabel: "Our MIN PMAX Ads package",
        channels: ["PMAX Ads"],
        bestFor:
          "PMAX Ads: reach travelers across all of Google.",
        tagline: "Convert dreamers into visitors.",
        bullets: [
          "Runs across Google Search, YouTube, Display, Discover, Gmail, and Maps — where travelers actually plan their trips",
          "Google AI optimizes creative and placement in real time — no more guessing what works",
          "Three targeting strategies working together: Search Themes (trip-planning intent), Category Targeting (travel behaviors + interests), and Retargeting (past site visitors)",
          "Geo-target out-of-state DMAs, drive markets, cities, zip codes, or a custom radius",
          "Cost-per-event campaign focused on bookings, ticket sales, and site conversions",
          "Retargets past visitors across every Google channel — ideal for stay-over conversion",
          "Includes container tag setup on your website (we handle it)",
        ],
      },
      {
        key: "social-spotlight",
        name: "Social Media",
        legacy: "PAID SOCIAL",
        price: "$4,500",
        minLabel: "Our MIN Paid Social package",
        channels: ["Social Spotlight"],
        bestFor:
          "Social Media: drive real results with a paid social media strategy.",
        tagline: "Organic social is dead. This is what works now.",
        taglineLinkText: "Here's why we say this",
        taglineLinkHref: "#why-organic-social",
        bullets: [
          "180,000 paid impressions on Facebook + Instagram",
          "Strategy includes Reels and Stories inventory",
          "15+ ad placements (Feed · Reels · Stories · Explore · Marketplace)",
          "Drives traffic straight to your website",
          "No followers required, paid reach works day one",
          "Creative refresh every 2 weeks to beat ad fatigue",
        ],
      },
      {
        key: "market-dominator",
        name: "Omni-Channel",
        legacy: "FULL FUNNEL",
        price: "$9,000",
        channels: ["Streaming TV", "PMAX Ads"],
        bestFor:
          "Omni-Channel: combine Streaming TV with PMAX Ads.",
        tagline: "Own the regional travel conversation.",
        bullets: [
          "Streaming TV brand-build across multiple states and DMAs",
          "PMAX Ads across Google Search, YouTube, Display, Discover, Gmail, and Maps",
          "Google AI targets active travel planners in real time",
          "Multiple concurrent campaign concepts supported",
          "Watch your Google Analytics spike as awareness turns into bookings",
          "First they learn about you on TV, then Google closes the loop",
          "Advertising the way it was meant to be done!",
        ],
        greenCallout: {
          title: "Omni Channel Starter Package",
          body:
            "Two strategies run concurrently, Streaming TV and PMAX Ads. Full customer journey: discovery to conversion.",
        },
      },
    ],
  },
};

const Packages = ({ vertical = "home-services" }) => {
  const config = VERTICAL_CONFIG[vertical] || VERTICAL_CONFIG["home-services"];

  return (
    <section
      id="packages"
      className="px-5 sm:px-8 py-24 max-w-7xl mx-auto"
      data-testid="packages-section"
    >
      <div className="mb-14 max-w-3xl">
        <div className="eyebrow mb-4">{config.sectionEyebrow}</div>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4">
          {config.sectionHeading}
        </h2>
        <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-2xl">
          {config.sectionSub}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
        {config.tiers.map((t) => {
          const highlighted = t.key === "call-now";
          return (
            <div
              key={t.key}
              className={`surface p-7 flex flex-col ${
                highlighted
                  ? "border-[var(--af-lime)]/50 ring-1 ring-[var(--af-lime)]/30"
                  : ""
              }`}
              data-testid={`package-card-${t.key}`}
            >
              <div className="flex items-baseline justify-between mb-4">
                <div className="font-eyebrow text-xs uppercase tracking-widest text-[var(--af-lime)]">
                  {t.name}
                </div>
                <div className="text-[10px] opacity-50 font-eyebrow uppercase tracking-widest">
                  ({t.legacy})
                </div>
              </div>
              <div className="font-display text-5xl lg:text-6xl mb-3">
                {t.price}
              </div>

              {t.channels && <ChannelStrip channels={t.channels} />}

              {t.bestFor && (
                <div
                  className="mb-5 rounded-lg border border-[var(--af-lime)]/30 bg-[var(--af-lime)]/[0.06] p-3"
                  data-testid={`package-best-for-${t.key}`}
                >
                  <div className="font-eyebrow text-[9px] uppercase tracking-widest text-[var(--af-lime)] mb-1">
                    Best for
                  </div>
                  <div className="text-[13px] leading-relaxed text-[var(--af-cream)]/90">
                    {t.bestFor}
                  </div>
                </div>
              )}

              <div className="text-sm mb-6 text-[var(--af-cream)]/70">
                {t.tagline}
                {t.taglineLinkText && (
                  <a
                    href={t.taglineLinkHref}
                    className="block mt-1.5 text-[var(--af-lime)] underline underline-offset-4 hover:no-underline text-xs"
                    data-testid={`package-tagline-link-${t.key}`}
                  >
                    {t.taglineLinkText} →
                  </a>
                )}
              </div>

              <ul className="space-y-3 mb-6 flex-1">
                {t.bullets.map((b, i) => (
                  <li key={i} className="flex gap-2.5 text-sm">
                    <Check
                      size={16}
                      className="mt-0.5 flex-shrink-0 text-[var(--af-lime)]"
                    />
                    <span className="text-[var(--af-cream)]/90">{b}</span>
                  </li>
                ))}
                {t.channels?.includes("Streaming TV") && (
                  <li className="flex text-sm pt-1">
                    <StreamingChannelsModal
                      triggerTestId={`package-streaming-channels-trigger-${t.key}`}
                    />
                  </li>
                )}
              </ul>

              {t.greenCallout && (
                <div
                  className="text-[11px] leading-relaxed border-t border-[var(--af-lime)]/30 pt-4 mt-3 text-[var(--af-cream)]/85"
                  data-testid={`package-green-callout-${t.key}`}
                >
                  <span className="font-eyebrow uppercase tracking-widest text-[var(--af-lime)] text-[10px] block mb-1">
                    {t.greenCallout.title}
                  </span>
                  {t.greenCallout.body}
                </div>
              )}

              {t.price === "$4,500" && (
                <div
                  className="text-[11px] leading-relaxed border-t border-[var(--af-lime)]/30 pt-4 mt-3 text-[var(--af-cream)]/70"
                  data-testid={`package-min-note-${t.key}`}
                >
                  <span className="font-eyebrow uppercase tracking-widest text-[var(--af-lime)] text-[10px] block mb-1">
                    {t.minLabel || "Our minimum package"}
                  </span>
                  3 months at $1,500/month, or 90 days at $4,500. Whichever
                  fits your campaign schedule best.
                </div>
              )}

              <a
                href="#lead-form"
                className={`mt-6 ${
                  highlighted ? "btn-lime" : "btn-ghost-lime"
                } justify-center`}
                data-testid={`package-cta-${t.key}`}
              >
                I&apos;d like to learn more <ArrowRight size={14} />
              </a>
            </div>
          );
        })}
      </div>

      <div className="mt-8">
        <Link
          to="/packages/faq"
          className="text-xs text-[var(--af-lime)] hover:underline underline-offset-4 inline-flex items-center gap-1.5 font-medium"
          data-testid="packages-faq-link"
        >
          Have questions about the packages? Read the FAQ <ArrowRight size={12} />
        </Link>
      </div>

      {/* Why organic social is dead — mini-explainer */}
      <div
        id="why-organic-social"
        className="mt-20 border-t border-[var(--af-line)] pt-16 scroll-mt-24"
        data-testid="organic-social-explainer"
      >
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">A Quick Note on Facebook &amp; Instagram</div>
            <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-4 leading-tight">
              Why we believe{" "}
              <span className="text-[var(--af-lime)]">organic social</span>{" "}
              is dead.
            </h3>
            <p className="text-base text-[var(--af-cream)]/75 leading-relaxed">
              Here&apos;s why we say it: because nobody else will tell you,
              but it&apos;s well-known industry knowledge. Facebook and
              Instagram pages are crucial for up-to-date information,
              branding, searching, and event postings. They&apos;re fun for
              Reels and Stories. But what they&apos;re not good for is
              advertising, which drives new business, awareness, and sales.
              If you need those three things, you must run a paid social
              media campaign today. Here&apos;s the math:
            </p>
          </div>
          {/* Right column: 40px top spacer pushes the box row down to align
              with the headline, then h-full + flex-1 stretches the boxes so
              their bottoms line up with the paragraph's last line. */}
          <div className="lg:col-span-7 flex flex-col">
            <div aria-hidden="true" className="hidden lg:block h-[40px]" />
            <div className="grid sm:grid-cols-3 gap-4 flex-1">
            {[
              {
                stat: "<3%",
                t: "Organic reach is gone",
                d: "The average Facebook page now reaches less than 3% of its own followers organically. Instagram is similar. Posting without paid support is shouting into a void.",
              },
              {
                stat: "100×",
                t: "Paid reach scales",
                d: "A single paid campaign at $4,500 can put your brand in front of 180,000+ targeted people. Organic posting, even with thousands of followers, can't match that math.",
              },
              {
                stat: "0",
                t: "Followers required",
                d: "Paid social doesn't care if you have 50 followers or 50,000. The targeting reaches the right people regardless. Building a follower count is no longer the bottleneck.",
              },
            ].map((b, i) => (
              <div
                key={i}
                className="surface p-5 h-full"
                data-testid={`organic-fact-${i}`}
              >
                <div className="font-display text-4xl text-[var(--af-lime)] leading-none mb-3">
                  {b.stat}
                </div>
                <div className="font-display text-lg mb-2 leading-tight">
                  {b.t}
                </div>
                <div className="text-sm text-[var(--af-cream)]/70 leading-relaxed">
                  {b.d}
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Packages;
